/* ═══════════════════════════════════════════════════════════════
   Wishifyy V2 - Main Product page enhancements (Dawn-based template)
   ───────────────────────────────────────────────────────────────
   1. Easify "step-by-step" progressive disclosure
      Locks all option cards except the first. Unlocks the next as
      each preceding option becomes valid (text filled / radio picked
      / image selected). Big space saver - user only sees ~1-2 active
      cards at a time instead of 11 stacked.
   2. Live progress indicator
      Injects a "Стъпка X от Y" counter + amber progress bar at the
      top of the .easify zone. Updates on every input event.
   3. ATC pulse
      Subtle "ready" pulse on the ATC when all required options are
      complete (visual nudge to convert).
   ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   USER-TOUCH TRACKING
   ───────────────────────────────────────────────────────────────
   Easify auto-selects the first radio / image swatch when its
   React tree mounts. That fires no user-driven input, but the
   radio's `checked` flag is set - so any naive validity check
   ("is one radio checked?") would falsely mark the step as
   completed before the customer has actively picked anything.

   Track REAL user interaction with a `data-mpv-user-touched`
   attribute set in capture-phase delegated listeners, so it runs
   *before* Easify's own handlers (and our progress logic) and
   covers every existing AND future option card without needing
   per-card listener attachment.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  function markTouched(e) {
    var card = e.target && e.target.closest && e.target.closest('.easify .po-option-root');
    if (card) card.setAttribute('data-mpv-user-touched', '1');
  }
  // pointerdown catches mouse + touch swatch picks (radios that swallow click)
  document.addEventListener('pointerdown', markTouched, { capture: true });
  document.addEventListener('click', markTouched, { capture: true });
  document.addEventListener('input', markTouched, { capture: true });
  document.addEventListener('change', markTouched, { capture: true });
})();

(function () {
  'use strict';

  /* Easify mounts asynchronously - retry until options exist (cap at ~10s). */
  var pollAttempts = 0;
  var maxPollAttempts = 40;

  /* Module-scoped refs so re-init can clean up the previous run instead of
     stacking observers/listeners (which caused crashes when Easify mutated
     the DOM heavily - e.g. on extra-fee option selection). */
  var currentObserver = null;
  var listenersAttached = new WeakSet();
  var reinitDebounce = null;

  /* Show all options at once when there are this many or fewer.
     Above the threshold, switch to step-by-step progressive disclosure
     so a 11-option product page doesn't feel like a wall. Bracelets and
     small-personalization products fall under the threshold and skip
     the lock/active/completed badges entirely. */
  var SHOW_ALL_THRESHOLD = 6;

  function init() {
    var easify = document.querySelector('.easify');
    if (!easify) {
      /* Easify mounts asynchronously after page load - keep polling. */
      if (pollAttempts++ < maxPollAttempts) setTimeout(init, 250);
      return;
    }

    var options = easify.querySelectorAll('.po-option-root');
    if (options.length === 0) {
      if (pollAttempts++ < maxPollAttempts) setTimeout(init, 250);
      return;
    }

    /* Tear down previous observer so successive inits don't stack */
    if (currentObserver) {
      try { currentObserver.disconnect(); } catch (e) {}
      currentObserver = null;
    }

    /* Few options → show them all unlocked, no step counter, no badges.
       Still observe option count for variant changes (e.g. selecting a
       gift-card swap that adds new fields) so we can re-evaluate the
       threshold and switch into step mode if needed. */
    if (options.length <= SHOW_ALL_THRESHOLD) {
      options.forEach(function (opt) {
        opt.classList.remove('is-locked', 'is-active', 'is-completed');
        opt.removeAttribute('data-mpv-step');
      });
      easify.querySelectorAll('[data-mpv-progress]').forEach(function (n) { n.remove(); });

      currentObserver = new MutationObserver(function () {
        if (reinitDebounce) return;
        reinitDebounce = setTimeout(function () {
          reinitDebounce = null;
          var nowOpts = easify.querySelectorAll('.po-option-root');
          if (nowOpts.length === options.length) return;
          pollAttempts = 0;
          init();
        }, 350);
      });
      currentObserver.observe(easify, { childList: true, subtree: true });
      return;
    }

    /* Inject the progress UI once */
    if (!easify.querySelector('[data-mpv-progress]')) {
      var progress = document.createElement('div');
      progress.className = 'mpv-progress';
      progress.setAttribute('data-mpv-progress', '');
      progress.innerHTML =
        '<div class="mpv-progress__head">' +
          '<span class="mpv-progress__label">Стъпка <span data-mpv-step-current>1</span> от <span data-mpv-step-total>' + options.length + '</span></span>' +
          '<span class="mpv-progress__hint" data-mpv-step-hint>Попълни първото поле, за да продължиш</span>' +
        '</div>' +
        '<div class="mpv-progress__bar"><div class="mpv-progress__fill" data-mpv-fill style="width: 0%"></div></div>';
      easify.insertBefore(progress, easify.firstChild);
    }

    var stepCurrentEl = easify.querySelector('[data-mpv-step-current]');
    var stepTotalEl = easify.querySelector('[data-mpv-step-total]');
    var fillEl = easify.querySelector('[data-mpv-fill]');
    var hintEl = easify.querySelector('[data-mpv-step-hint]');
    if (stepTotalEl) stepTotalEl.textContent = String(options.length);

    /* Wire option events ONCE per option (WeakSet prevents duplicate listeners
       on re-init, which previously caused 3-5x updateAll calls per click). */
    options.forEach(function (opt, idx) {
      if (idx > 0 && !opt.classList.contains('is-completed') && !opt.classList.contains('is-active')) {
        opt.classList.add('is-locked');
      }
      opt.setAttribute('data-mpv-step', String(idx + 1));

      if (!listenersAttached.has(opt)) {
        listenersAttached.add(opt);
        ['input', 'change', 'click'].forEach(function (ev) {
          opt.addEventListener(ev, function () {
            /* slight delay so radios/swatches register their state before we read */
            setTimeout(function () {
              try {
                updateAll(easify, easify.querySelectorAll('.po-option-root'), stepCurrentEl, stepTotalEl, fillEl, hintEl);
              } catch (e) { /* swallow - never crash the page */ }
            }, 80);
          });
        });
      }
    });

    try { updateAll(easify, options, stepCurrentEl, stepTotalEl, fillEl, hintEl); } catch (e) {}

    /* Асинхронните качвания (флипбук: „Прикачете 8 снимки") не пращат
       input/change върху опцията - Easify просто слага миниатюрите в DOM-а.
       Затова стъпката ставаше валидна, но нищо не преизчисляваше и следващата
       оставаше заключена, докато потребителят не кликне някъде. Тук следим
       „подписа" на валидността и преизчисляваме само когато се е сменил.
       updateAll мени само класове, не валидност → няма зациклане. */
    if (!window.__mpvValiditySignature) {
      window.__mpvValiditySignature = true;
      var lastSig = null;
      setInterval(function () {
        try {
          var opts = easify.querySelectorAll('.po-option-root');
          if (!opts.length) return;
          /* В подписа влиза и дали полето вече има file input: Easify дорендира
             полетата за качване след първия updateAll, а групирането на двете
             полета за снимки зависи точно от това. */
          var sig = Array.prototype.map.call(opts, function (o) {
            var f = '-';
            try { f = o.querySelector('input[type=file]') ? 'f' : '-'; } catch (e) {}
            return (isValid(o) ? '1' : '0') + f;
          }).join('');
          if (lastSig === null) { lastSig = sig; return; }
          if (sig === lastSig) return;
          lastSig = sig;
          updateAll(easify, easify.querySelectorAll('.po-option-root'),
                    stepCurrentEl, stepTotalEl, fillEl, hintEl);
        } catch (e) { /* никога не чупи страницата */ }
      }, 500);
    }

    /* Single observer, debounced. Only re-init when option COUNT changes
       (variant swap adding/removing fields). Price-modifier mutations on
       option select don't change count → ignored.

       When count INCREASES (Easify spawns dependent fields after the user
       picks an option that unlocks more - e.g. picking the gift card
       swatch reveals "what to write on the card"), tag the brand-new
       options with `data-mpv-spawn` so updateAll keeps them unlocked
       even if they land past an unfilled step. Without this the spawned
       row appears, then snaps to is-locked (max-height:0) → flicker. */
    var prevOptArray = Array.prototype.slice.call(options);
    currentObserver = new MutationObserver(function () {
      if (reinitDebounce) return;
      reinitDebounce = setTimeout(function () {
        reinitDebounce = null;
        var nowOpts = easify.querySelectorAll('.po-option-root');
        if (nowOpts.length === prevOptArray.length) return;

        if (nowOpts.length > prevOptArray.length) {
          // Mark newcomers as spawn so they stay visible
          var oldSet = new WeakSet();
          prevOptArray.forEach(function (o) { oldSet.add(o); });
          nowOpts.forEach(function (opt) {
            if (!oldSet.has(opt)) opt.setAttribute('data-mpv-spawn', '1');
          });
        }

        // Full re-init handles ordering/index changes correctly. Spawn
        // markers persist on the new nodes so updateAll won't lock them.
        easify.querySelectorAll('[data-mpv-progress]').forEach(function (n) { n.remove(); });
        nowOpts.forEach(function (n) {
          n.classList.remove('is-locked', 'is-completed', 'is-active');
          n.removeAttribute('data-mpv-step');
        });
        prevOptArray = Array.prototype.slice.call(nowOpts);
        pollAttempts = 0;
        init();
      }, 350);
    });
    /* subtree: true is required - Easify spawns new .po-option-root nodes
       deep in the React tree (e.g. when an extra-fee option triggers a
       dependent card to appear). Debounce + count-check guards the loop. */
    currentObserver.observe(easify, { childList: true, subtree: true });
  }

  /* Patch updateAll to keep variant-spawned options visible.
     Original behavior locks anything past the first un-valid step.
     Easify spawns dependent fields (e.g. "what to write on the card"
     after picking the gift card swatch) deeper in the option list.
     If those new fields land past an unfilled font-picker, they get
     locked and the user sees a 1-frame flicker as `is-locked` snaps
     in (max-height: 0). We tag spawned options with `data-mpv-spawn`
     in the observer below and skip the lock branch for them. */
  var origUpdateAll = updateAll;
  updateAll = function (easify, options, stepCurrentEl, stepTotalEl, fillEl, hintEl) {
    var validBefore = true;
    var completedCount = 0;
    var firstUnlockedIdx = 0;
    options.forEach(function (opt, idx) {
      var thisValid = isValid(opt);
      var spawned = opt.hasAttribute('data-mpv-spawn');
      if (validBefore || idx === 0 || spawned) {
        opt.classList.remove('is-locked');
        if (thisValid) {
          opt.classList.add('is-completed');
          opt.classList.remove('is-active');
          completedCount++;
        } else {
          opt.classList.remove('is-completed');
          opt.classList.add('is-active');
          if (firstUnlockedIdx === 0) firstUnlockedIdx = idx;
        }
      } else {
        opt.classList.add('is-locked');
        opt.classList.remove('is-completed', 'is-active');
      }
      validBefore = validBefore && thisValid;
    });
    var activeOnes = easify.querySelectorAll('.po-option-root.is-active');
    if (activeOnes.length > 1) {
      for (var k = 1; k < activeOnes.length; k++) activeOnes[k].classList.remove('is-active');
    }
    if (fillEl) {
      var pct = options.length > 0 ? (completedCount / options.length) * 100 : 0;
      fillEl.style.width = pct + '%';
    }
    if (stepCurrentEl) stepCurrentEl.textContent = String(Math.min(completedCount + 1, options.length));
    if (hintEl) {
      if (completedCount === options.length) {
        hintEl.textContent = '✓ Всички полета попълнени - готов си!';
        hintEl.classList.add('is-done');
      } else if (completedCount === 0) {
        hintEl.textContent = 'Попълни първото поле, за да продължиш';
        hintEl.classList.remove('is-done');
      } else {
        hintEl.textContent = 'Остават още ' + (options.length - completedCount) + ' стъпки';
        hintEl.classList.remove('is-done');
      }
    }
    var atc = document.querySelector('.product-form__submit, button[name="add"].button--primary');
    if (atc) {
      if (completedCount === options.length && options.length > 0) atc.classList.add('is-ready');
      else atc.classList.remove('is-ready');
    }
  };

  /* Validity check - has the user actually answered this option?
     Strategy: only count as VALID if we have positive evidence of input
     (filled text, checked radio, selected option, etc.). Optional fields
     that have no current value also count as valid (so they don't block
     progress for the next required field). Required fields without
     values stay invalid. */
  function isValid(opt) {
    var isRequired = !!(
      opt.querySelector('.po-label-required') ||
      opt.querySelector('[aria-required="true"]') ||
      opt.querySelector('input[required], textarea[required], select[required]')
    );

    // Text inputs / textareas
    var textInputs = opt.querySelectorAll('input[type="text"], input[type="email"], input[type="number"], input[type="tel"], textarea');
    var hasFilledText = false;
    var hasEmptyText = false;
    for (var i = 0; i < textInputs.length; i++) {
      if (textInputs[i].value && textInputs[i].value.trim()) hasFilledText = true;
      else hasEmptyText = true;
    }
    // Radios / checkboxes
    var radios = opt.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    var hasCheckedRadio = false;
    for (var j = 0; j < radios.length; j++) { if (radios[j].checked) { hasCheckedRadio = true; break; } }
    // Selects
    var selects = opt.querySelectorAll('select');
    var hasSelected = false;
    var hasUnselected = false;
    for (var k = 0; k < selects.length; k++) {
      var sv = selects[k].value;
      if (sv && sv !== '') hasSelected = true;
      else hasUnselected = true;
    }
    // ARIA-driven swatches (Easify image picker)
    var ariaRadios = opt.querySelectorAll('[role="radio"]');
    var ariaChecked = opt.querySelectorAll('[aria-checked="true"]');
    var hasAriaChecked = ariaRadios.length > 0 && ariaChecked.length > 0;
    var hasAriaUnchecked = ariaRadios.length > 0 && ariaChecked.length === 0;
    // File uploads - Easify clears the native <input type=file>.files
    // after handling the upload (state lives in React; URLs in hidden
    // properties[*] inputs). The reliable post-upload indicator is the
    // rendered thumbnail items (`.po-fileUpload-uploadItem`).
    var files = opt.querySelectorAll('input[type="file"]');
    var uploadedItems = opt.querySelectorAll('.po-fileUpload-uploadItem');
    var hasUploadedFile = uploadedItems.length > 0;
    if (!hasUploadedFile) {
      for (var f = 0; f < files.length; f++) {
        if (files[f].files && files[f].files.length > 0) { hasUploadedFile = true; break; }
      }
    }
    var hasEmptyFile = files.length > 0 && !hasUploadedFile;
    // Easify React-only swatches (image / color / button / font pickers).
    // They render as <div> trees with no <input> we can introspect - state
    // lives in React. Treat them as a control type and require user touch
    // (handled below) for completion.
    var hasReactSwatch = !!opt.querySelector(
      '[class*="po-imageSwatches"],' +
      '[class*="po-colorSwatches"],' +
      '[class*="po-buttonSwatches"],' +
      '[class*="po-fontPickerSwatches"],' +
      '[class*="po-textSwatches"]'
    );

    // Has any controls at all?
    var hasAnyControl = textInputs.length > 0 || radios.length > 0 || selects.length > 0 || ariaRadios.length > 0 || files.length > 0 || hasReactSwatch;
    if (!hasAnyControl) {
      // Decorative card (label-only, no input) - treat as completed so
      // it doesn't block subsequent required cards.
      return true;
    }

    // User must have actively interacted with this card. Easify auto-
    // selects defaults at mount, so a `checked` radio / `aria-checked`
    // swatch by itself isn't enough - it must be from a real user
    // pointerdown/click/input event (tracked above).
    var touched = opt.hasAttribute('data-mpv-user-touched');
    if (!touched) {
      // Untouched OPTIONAL fields are skippable - they must NOT block the
      // reveal of subsequent required fields. Previously `return false` here
      // marked optional+empty as invalid, which set validBefore=false and
      // hid every required step that followed. Customer hit ATC, got
      // "fill required field" error, but the field was locked off-screen.
      // Untouched REQUIRED fields still return false (they DO block).
      return !isRequired;
    }

    // Required: all controls must be answered
    if (isRequired) {
      if (textInputs.length > 0 && hasEmptyText) return false;
      if (radios.length > 0 && !hasCheckedRadio) return false;
      if (selects.length > 0 && hasUnselected) return false;
      if (ariaRadios.length > 0 && hasAriaUnchecked) return false;
      if (files.length > 0 && hasEmptyFile) return false;
      return true;
    }

    // Touched + optional → always valid. The user has interacted (so we
    // know they considered this step) and they're free to leave it blank
    // or fill anything they want; either way it doesn't block progress.
    return true;
  }

  /* Полетата за качване вървят по двойки (флипбукът иска 16 снимки, а Easify
   ги дели на 2 по 8). Това е ЕДНО действие за клиента, затова второто поле не
   бива да е отделна заключена стъпка - показваме ги заедно. */
  function mpvIsFileOption(opt) {
    try { return !!opt && !!opt.querySelector('input[type="file"]'); }
    catch (e) { return false; }
  }

  function updateAll(easify, options, stepCurrentEl, stepTotalEl, fillEl, hintEl) {
    var validBefore = true;
    var completedCount = 0;
    var firstUnlockedIdx = 0;

    options.forEach(function (opt, idx) {
      var thisValid = isValid(opt);
      /* съседно поле за качване → същата стъпка, не заключвай */
      var groupedWithPrev = idx > 0 && mpvIsFileOption(opt) && mpvIsFileOption(options[idx - 1]);
      if (validBefore || idx === 0 || groupedWithPrev) {
        opt.classList.remove('is-locked');
        if (thisValid) {
          opt.classList.add('is-completed');
          opt.classList.remove('is-active');
          completedCount++;
        } else {
          opt.classList.remove('is-completed');
          opt.classList.add('is-active');
          if (firstUnlockedIdx === 0) firstUnlockedIdx = idx;
        }
      } else {
        opt.classList.add('is-locked');
        opt.classList.remove('is-completed', 'is-active');
      }
      validBefore = validBefore && thisValid;
    });

    /* Make sure exactly ONE card is is-active (the one currently editable) */
    var activeOnes = easify.querySelectorAll('.po-option-root.is-active');
    if (activeOnes.length > 1) {
      for (var k = 1; k < activeOnes.length; k++) activeOnes[k].classList.remove('is-active');
    }

    /* Update progress UI */
    if (fillEl) {
      var pct = options.length > 0 ? (completedCount / options.length) * 100 : 0;
      fillEl.style.width = pct + '%';
    }
    if (stepCurrentEl) stepCurrentEl.textContent = String(Math.min(completedCount + 1, options.length));
    if (hintEl) {
      if (completedCount === options.length) {
        hintEl.textContent = '✓ Всички полета попълнени - готов си!';
        hintEl.classList.add('is-done');
      } else if (completedCount === 0) {
        hintEl.textContent = 'Попълни първото поле, за да продължиш';
        hintEl.classList.remove('is-done');
      } else {
        hintEl.textContent = 'Остават още ' + (options.length - completedCount) + ' стъпки';
        hintEl.classList.remove('is-done');
      }
    }

    /* ATC pulse - when all done, add .is-ready class for CSS pulse */
    var atc = document.querySelector('.product-form__submit, button[name="add"].button--primary');
    if (atc) {
      if (completedCount === options.length && options.length > 0) atc.classList.add('is-ready');
      else atc.classList.remove('is-ready');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ═══════════════════════════════════════════════════════════════
   GALLERY MAGNIFIER - click .mpv2__gallery-zoom opens a lightbox
   with the currently-active image. Arrow keys / on-screen buttons
   navigate between gallery images. ESC / backdrop click closes.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function initZoom() {
    var zoomBtn = document.querySelector('.mpv2__gallery-zoom');
    if (!zoomBtn) return;

    var imgs = Array.from(document.querySelectorAll('.mpv2__gallery-img'));
    if (imgs.length === 0) return;

    var lightbox = null;
    var lbImg = null;
    var lbCounter = null;
    var currentIdx = 0;

    function buildLightbox() {
      if (lightbox) return;
      lightbox = document.createElement('div');
      lightbox.className = 'mpv2-zoom-lightbox';
      lightbox.setAttribute('hidden', '');
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.innerHTML =
        '<button type="button" class="mpv2-zoom-lightbox__close" aria-label="Затвори">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
        (imgs.length > 1 ?
          '<button type="button" class="mpv2-zoom-lightbox__nav mpv2-zoom-lightbox__nav--prev" aria-label="Предишна">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
          '</button>' +
          '<button type="button" class="mpv2-zoom-lightbox__nav mpv2-zoom-lightbox__nav--next" aria-label="Следваща">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>' +
          '</button>' +
          '<span class="mpv2-zoom-lightbox__counter" data-counter></span>' : '') +
        '<img class="mpv2-zoom-lightbox__img" alt="">';
      document.body.appendChild(lightbox);
      lbImg = lightbox.querySelector('.mpv2-zoom-lightbox__img');
      lbCounter = lightbox.querySelector('[data-counter]');

      lightbox.querySelector('.mpv2-zoom-lightbox__close').addEventListener('click', close);
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) close();
      });
      var prevBtn = lightbox.querySelector('.mpv2-zoom-lightbox__nav--prev');
      var nextBtn = lightbox.querySelector('.mpv2-zoom-lightbox__nav--next');
      if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); show(currentIdx - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); show(currentIdx + 1); });
    }

    function show(idx) {
      var n = imgs.length;
      currentIdx = ((idx % n) + n) % n;
      var src = imgs[currentIdx].getAttribute('src') || '';
      // Upgrade image_url to a larger render - img URLs end with `width=1200`
      // or have a `1200x` segment we can swap for `2000x` for higher quality.
      var bigSrc = src.replace(/width=\d+/, 'width=2000').replace(/_(\d+)x(?=\.)/, '_2000x');
      lbImg.src = bigSrc || src;
      lbImg.alt = imgs[currentIdx].getAttribute('alt') || '';
      if (lbCounter) lbCounter.textContent = (currentIdx + 1) + ' / ' + n;
    }

    function open() {
      buildLightbox();
      // Sync to current active gallery image
      var activeIdx = imgs.findIndex(function (i) { return i.classList.contains('active'); });
      show(activeIdx >= 0 ? activeIdx : 0);
      lightbox.removeAttribute('hidden');
      document.body.classList.add('mpv2-zoom-open');
    }

    function close() {
      if (!lightbox) return;
      lightbox.setAttribute('hidden', '');
      document.body.classList.remove('mpv2-zoom-open');
    }

    zoomBtn.addEventListener('click', open);
    /* Click anywhere on the gallery image area opens the lightbox.
       Bind on the parent container instead of individual images - non-active
       images sit on top with opacity:0 (still clickable), so per-image
       handlers fired only on whichever invisible image happened to be on top. */
    var galleryMain = document.querySelector('.mpv2__gallery-main');
    if (galleryMain) {
      galleryMain.style.cursor = 'zoom-in';
      galleryMain.addEventListener('click', function (e) {
        // Don't double-fire if the zoom button itself was clicked
        if (e.target.closest('.mpv2__gallery-zoom')) return;
        open();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!lightbox || lightbox.hasAttribute('hidden')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft' && imgs.length > 1) show(currentIdx - 1);
      else if (e.key === 'ArrowRight' && imgs.length > 1) show(currentIdx + 1);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initZoom);
  else initZoom();
})();

/* ═══════════════════════════════════════════════════════════════
   BACK-IN-STOCK SUBSCRIBE FORM
   ───────────────────────────────────────────────────────────────
   On sold-out products, the cart form is replaced with a "Notify me"
   form. This handler hijacks submit and POSTs to the CRM Worker which
   stores the email, creates/updates a Shopify customer (so the address
   joins the marketing list), sends a confirmation email immediately,
   and queues a back-in-stock email for the cron to fire once stock
   returns.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  function init() {
    var form = document.querySelector('[data-mpv2-bis-form]');
    if (!form) return;

    var msg = form.querySelector('[data-mpv2-bis-msg]');
    var btn = form.querySelector('.mpv2__bis-btn');

    function show(text, kind) {
      if (!msg) return;
      msg.textContent = text;
      msg.classList.remove('is-success', 'is-error');
      msg.classList.add('is-visible', kind === 'error' ? 'is-error' : 'is-success');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (form.querySelector('input[name="email"]') || {}).value || '';
      email = email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        show('Моля, въведи валиден имейл адрес.', 'error');
        return;
      }
      var marketing = !!form.querySelector('input[name="marketing_opt_in"]').checked;

      var d = form.dataset;
      var workerBase = (d.workerBase || 'https://wishifyy-crm.wishifycom.workers.dev').replace(/\/+$/, '');
      var payload = {
        email: email,
        marketing_opt_in: marketing,
        product_id: parseInt(d.productId, 10) || null,
        product_handle: d.productHandle || '',
        product_title: d.productTitle || '',
        variant_id: parseInt(d.variantId, 10) || null,
        product_url: d.productUrl || '',
        product_image: d.productImage || '',
        locale: (document.documentElement.lang || 'bg').slice(0, 2),
        source: 'product-page'
      };

      btn.disabled = true;
      var btnLabel = btn.querySelector('span');
      var origLabel = btnLabel ? btnLabel.textContent : '';
      if (btnLabel) btnLabel.textContent = 'Изпращане…';

      fetch(workerBase + '/api/back-in-stock/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) {
          return r.json().catch(function () { return { ok: r.ok }; });
        })
        .then(function (data) {
          if (data && (data.ok || data.success || data.subscribed)) {
            show('✓ Записахме те. Ще получиш потвърждение на имейла, и съобщение веднага щом продуктът е отново наличен.', 'success');
            form.querySelector('input[name="email"]').value = '';
          } else {
            show((data && data.error) || 'Нещо се обърка. Моля, опитай пак след малко.', 'error');
          }
        })
        .catch(function () {
          show('Връзката с нашия сървър прекъсна. Опитай пак след малко.', 'error');
        })
        .finally(function () {
          btn.disabled = false;
          if (btnLabel) btnLabel.textContent = origLabel;
        });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ═══════════════════════════════════════════════════════════════
   STICKY/ATC EASIFY-AWARE VALIDATION
   ───────────────────────────────────────────────────────────────
   Dawn's product-form custom element only checks browser-native
   `[required]` inputs on submit. Easify renders custom radio/swatch
   pickers + non-required text fields without that attribute, so a
   user could click ATC without picking an engraving font and the
   form would silently submit. Intercept the click in the CAPTURE
   phase, run our own Easify completeness check, and if anything's
   missing show a toast + scroll-and-flash on the personalization
   zone (per user request: "ако не - просто да ги връща при
   персонализациите и да им намеква че трябва да ги попълнят").
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function showHint(msg) {
    var hint = document.querySelector('.mpv2-atc-hint');
    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'mpv2-atc-hint';
      document.body.appendChild(hint);
    }
    hint.textContent = msg || 'Попълни персонализацията преди да добавиш в количка';
    requestAnimationFrame(function () { hint.classList.add('is-visible'); });
    clearTimeout(showHint._t);
    showHint._t = setTimeout(function () {
      hint.classList.remove('is-visible');
    }, 3200);
  }

  function easifyMissingRequiredCards() {
    var easify = document.querySelector('.easify');
    if (!easify) return []; // no Easify on page = nothing to validate
    var cards = easify.querySelectorAll('.po-option-root');
    var missing = [];
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var required = !!(
        c.querySelector('.po-label-required') ||
        c.querySelector('[aria-required="true"]') ||
        c.querySelector('input[required], textarea[required], select[required]')
      );
      if (!required) continue;

      var texts = c.querySelectorAll('input[type="text"], input[type="email"], input[type="number"], input[type="tel"], textarea');
      var radios = c.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      var selects = c.querySelectorAll('select');
      var ariaRadios = c.querySelectorAll('[role="radio"]');
      var files = c.querySelectorAll('input[type="file"]');
      var reactSwatch = !!c.querySelector(
        '[class*="po-imageSwatches"],[class*="po-colorSwatches"],[class*="po-buttonSwatches"],[class*="po-fontPickerSwatches"],[class*="po-textSwatches"]'
      );
      var hasControl = texts.length || radios.length || selects.length || ariaRadios.length || files.length || reactSwatch;
      if (!hasControl) continue;

      // User must have ACTIVELY picked / typed in this required card.
      // Auto-checked Easify defaults don't count.
      if (!c.hasAttribute('data-mpv-user-touched')) { missing.push(c); continue; }

      var textOk = true;
      for (var t = 0; t < texts.length; t++) {
        if (!texts[t].value || !texts[t].value.trim()) { textOk = false; break; }
      }
      var radioOk = radios.length === 0;
      for (var r = 0; r < radios.length; r++) { if (radios[r].checked) { radioOk = true; break; } }
      var selectOk = true;
      for (var s = 0; s < selects.length; s++) { if (!selects[s].value) { selectOk = false; break; } }
      var ariaOk = ariaRadios.length === 0 || c.querySelectorAll('[aria-checked="true"]').length > 0;
      var fileOk;
      if (files.length === 0) {
        fileOk = true;
      } else {
        var uploadedItems = c.querySelectorAll('.po-fileUpload-uploadItem');
        if (uploadedItems.length > 0) {
          fileOk = true;
        } else {
          fileOk = false;
          for (var f = 0; f < files.length; f++) {
            if (files[f].files && files[f].files.length > 0) { fileOk = true; break; }
          }
        }
      }

      if (!textOk || !radioOk || !selectOk || !ariaOk || !fileOk) missing.push(c);
    }
    return missing;
  }
  function easifyMissingRequired() { return easifyMissingRequiredCards().length > 0; }

  function clearMpvErrors() {
    var marked = document.querySelectorAll('.po-option-root.mpv-error');
    for (var i = 0; i < marked.length; i++) marked[i].classList.remove('mpv-error');
  }

  function flashEasify(missingCards) {
    var zone = document.getElementById('mpv2-easify-mask') || document.querySelector('.easify');
    if (!zone) return;
    clearMpvErrors();
    var cards = missingCards && missingCards.length ? missingCards : null;
    if (cards) {
      for (var i = 0; i < cards.length; i++) cards[i].classList.add('mpv-error');
      // Locked steps may be max-height:0 - unlock the first missing card so
      // the customer can actually see what's wrong after the scroll lands.
      var first = cards[0];
      if (first.classList.contains('is-locked')) {
        first.classList.remove('is-locked');
        first.classList.add('is-active');
      }
      try { first.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { first.scrollIntoView(); }
    } else {
      zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(function () {
      zone.classList.add('flash');
      setTimeout(function () { zone.classList.remove('flash'); }, 1400);
    }, 380);
  }

  // Clear the error highlight as soon as the customer interacts with a
  // previously-flagged card. Capture phase + bubble so we catch both Easify's
  // own React handlers and native input events.
  function clearOnTouch(e) {
    var card = e.target && e.target.closest && e.target.closest('.po-option-root.mpv-error');
    if (card) card.classList.remove('mpv-error');
  }
  document.addEventListener('pointerdown', clearOnTouch, { capture: true });
  document.addEventListener('input', clearOnTouch, { capture: true });
  document.addEventListener('change', clearOnTouch, { capture: true });

  /* ─── EASIFY MODAL (triggered by sticky ATC) ─── */
  var modalOpen = false;

  function findRealEasify() {
    /* Easify can mount its UI in a sibling `.easify` portal outside our
       `.mpv2__easify-zone` placeholder. Find the one that actually holds
       the option cards. */
    var all = document.querySelectorAll('.easify');
    for (var i = 0; i < all.length; i++) {
      if (all[i].querySelector('.po-option-root')) return all[i];
    }
    return null;
  }

  function ensureModalChrome(target) {
    if (!target) return null;

    // Backdrop overlay (fixed under the modal). Insert at body root.
    if (!document.querySelector('.mpv2-easify-modal-overlay')) {
      var overlay = document.createElement('div');
      overlay.className = 'mpv2-easify-modal-overlay';
      overlay.addEventListener('click', closeEasifyModal);
      document.body.appendChild(overlay);
    }

    // Header + close button + sticky submit footer - injected once per target
    if (!target.querySelector('[data-mpv-modal-close]')) {
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'mpv2-easify-modal-close';
      close.setAttribute('data-mpv-modal-close', '');
      close.setAttribute('aria-label', 'Затвори');
      close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      close.addEventListener('click', closeEasifyModal);
      target.appendChild(close);

      var header = document.createElement('div');
      header.className = 'mpv2-easify-modal-header';
      header.innerHTML =
        '<span class="mpv2-easify-modal-eyebrow">Персонализация</span>' +
        '<span class="mpv2-easify-modal-title">Направи го свой</span>';
      target.insertBefore(header, target.firstChild);

      var footer = document.createElement('div');
      footer.className = 'mpv2-easify-modal-submit';
      footer.innerHTML =
        '<button type="button" class="mpv2-easify-modal-submit-btn">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M3 6h18l-2 12H5z"/><path d="M8 6V4a4 4 0 0 1 8 0v2"/></svg>' +
          '<span>Добави в количка</span>' +
        '</button>';
      footer.querySelector('button').addEventListener('click', function () {
        var missing = easifyMissingRequiredCards();
        if (missing.length) {
          showHint();
          flashEasify(missing);
          return;
        }
        closeEasifyModal();
        // Hand off to the real form submit (AJAX → cart drawer via product-form)
        var mainAtc = document.querySelector('.mpv2__btn-atc, button[name="add"], .product-form__submit');
        if (mainAtc) mainAtc.click();
      });
      target.appendChild(footer);
    }
    return target;
  }

  function openEasifyModal() {
    if (modalOpen) return;
    var target = findRealEasify();
    if (!ensureModalChrome(target)) return;
    target.classList.add('mpv-modal-active');
    modalOpen = true;
    document.body.classList.add('mpv2-easify-modal');
    document.addEventListener('keydown', escClose);
  }

  function closeEasifyModal() {
    if (!modalOpen) return;
    modalOpen = false;
    document.body.classList.remove('mpv2-easify-modal');
    document.querySelectorAll('.easify.mpv-modal-active').forEach(function (e) {
      e.classList.remove('mpv-modal-active');
    });
    document.removeEventListener('keydown', escClose);
  }

  function escClose(e) { if (e.key === 'Escape') closeEasifyModal(); }

  /* ─── CLICK INTERCEPTORS ─── */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    // STICKY ATC always opens the modal (lets the user fill their
    // personalization without scrolling back to the hero zone).
    var sticky = e.target.closest('.mpv2__sticky-atc, [data-mpv2-sticky-atc], .mpv2__sticky-btn');
    if (sticky) {
      // If there's no Easify on this product, fall through to a direct submit.
      var hasEasify = !!document.querySelector('.easify .po-option-root');
      if (hasEasify) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openEasifyModal();
        return;
      }
    }

    // MAIN ATC - block submit + flash if Easify required fields are unanswered
    var atc = e.target.closest('.mpv2__btn-atc, button[name="add"], .product-form__submit');
    if (!atc) return;
    // Don't block clicks coming from the modal's own submit button
    if (e.target.closest('.mpv2-easify-modal-submit-btn')) return;
    var missingCards = easifyMissingRequiredCards();
    if (!missingCards.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    showHint();
    flashEasify(missingCards);
  }, { capture: true });
})();

/* ═══════════════════════════════════════════════════════════════
   CART DRAWER NULL-SAFETY PATCH
   ───────────────────────────────────────────────────────────────
   Dawn's cart-drawer.renderContents() iterates getSectionsToRender()
   and assigns `.innerHTML` for each, blowing up with TypeError if
   any expected DOM target is missing. The Wishifyy header-v2 doesn't
   render a `#cart-icon-bubble` element (Dawn convention), so the
   stock implementation crashes mid-render - drawer never opens, ATC
   appears to silently "do nothing" even though the item is added.

   Replace renderContents with a defensive version that skips missing
   targets and still opens the drawer. Patches the prototype after
   the original `cart-drawer.js` has registered the custom element.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function patch() {
    var Ctor = (typeof customElements !== 'undefined') && customElements.get('cart-drawer');
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__mpvPatched) return false;

    Ctor.prototype.__mpvPatched = true;
    Ctor.prototype.renderContents = function (parsedState) {
      try {
        var inner = this.querySelector('.drawer__inner');
        if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
        this.productId = parsedState && parsedState.id;
        var self = this;
        var sections = this.getSectionsToRender();
        sections.forEach(function (section) {
          var el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el) return; // skip missing targets (e.g. header-v2 has no #cart-icon-bubble)
          var html = parsedState && parsedState.sections && parsedState.sections[section.id];
          if (typeof html !== 'string') return;
          try {
            el.innerHTML = self.getSectionInnerHTML(html, section.selector);
          } catch (e) { /* malformed section HTML - leave existing markup */ }
        });
        setTimeout(function () {
          var overlay = self.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', self.close.bind(self));
          self.open();
        });
      } catch (err) {
        // Last-resort: just open the drawer so the user sees something happened
        try { this.open(); } catch (e) {}
      }
    };
    return true;
  }

  if (!patch()) {
    // cart-drawer.js may not have registered yet - retry briefly
    var tries = 0;
    var iv = setInterval(function () {
      if (patch() || ++tries > 20) clearInterval(iv);
    }, 250);
  }
})();


/* ── Сгъване на списъка с качени снимки ─────────────────────────────────
   Всеки .po-fileUpload-imageListing с 2+ реда се сгъва до първия ред + бутон
   „Виж всичките N снимки". Работи през интервал, защото Easify рендира и
   пре-рендира списъка при всяко качване/триене. */
(function () {
  if (window.__mpvCollapseUploads) return;
  window.__mpvCollapseUploads = true;
  function mpvCollapseUploads() {
    document.querySelectorAll('.easify .po-fileUpload-imageListing').forEach(function (list) {
      var items = list.querySelectorAll('.po-fileUpload-uploadItem');
      var toggle = list.parentElement.querySelector('.mpv-uploads-toggle');
      if (items.length < 2) {
        list.classList.remove('mpv-uploads-collapsed');
        if (toggle) toggle.remove();
        return;
      }
      /* по подразбиране - сгънат; ръчното разгъване се пази на елемента */
      if (!list.hasAttribute('data-mpv-expanded')) list.classList.add('mpv-uploads-collapsed');
      var label = list.classList.contains('mpv-uploads-collapsed')
        ? ('Виж всичките ' + items.length + ' снимки')
        : 'Скрий снимките';
      if (!toggle) {
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mpv-uploads-toggle';
        toggle.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          var collapsed = list.classList.toggle('mpv-uploads-collapsed');
          if (collapsed) list.removeAttribute('data-mpv-expanded');
          else list.setAttribute('data-mpv-expanded', '1');
          mpvCollapseUploads();
        });
        list.parentElement.appendChild(toggle);
      }
      toggle.textContent = label;
    });
  }
  setInterval(mpvCollapseUploads, 700);
})();


/* ── Глобална бърза гравюра ──────────────────────────────────────────────
   Опцията е на всеки гравиран продукт, но е услуга ЗА ПОРЪЧКАТА: избере ли я
   клиентът веднъж (има ред в количката с property „Бърза гравюра"), на другите
   продуктови страници тя се заключва с обяснение - иначе би платил такса за
   всеки продукт. Отключва се сама, ако редът излезе от количката. */
(function () {
  if (window.__mpvExpressLock) return;
  window.__mpvExpressLock = true;
  var EXPRESS_RX = /бърза гравюра|спешен случай/i;
  var cartHasExpress = null;   // null = още не знаем

  function findExpressOption() {
    var opts = document.querySelectorAll('.easify .po-option-root');
    for (var i = 0; i < opts.length; i++) {
      var label = opts[i].querySelector('.po-label-label');
      if (label && EXPRESS_RX.test(label.textContent || '')) return opts[i];
    }
    return null;
  }

  function isOptionOn(opt) {
    /* Easify toggle-ът няма aria state - разчитаме на priceSummary плочката,
       която се появява само при включена платена опция в ТОЗИ продукт. */
    return !!document.querySelector('.easify .po-priceSummary-root');
  }

  function pageExpressOn() {
    var opt = findExpressOption();
    return !!(opt && opt.querySelector('input:checked'));
  }
  /* „3-4 работни дни" <-> „1-2 работни дни" в бележката под Добави в количка:
     двата span-а идват от Liquid (mpv2-days-std / mpv2-days-exp). Активира се
     при избрана бърза гравюра на ТАЗИ страница или вече в количката. */
  function applyDays() {
    var std = document.querySelector('.mpv2-days-std');
    var exp = document.querySelector('.mpv2-days-exp');
    if (!std || !exp) return;
    var active = !!cartHasExpress || pageExpressOn();
    std.hidden = active;
    exp.hidden = !active;
  }
  document.addEventListener('change', function () { setTimeout(applyDays, 60); }, true);

  function apply() {
    applyDays();
    var opt = findExpressOption();
    if (!opt) return;
    var locked = opt.classList.contains('mpv-express-locked');
    /* Ако клиентът е включил опцията НА ТАЗИ страница, не я заключвай -
       това е самият продукт, който носи таксата. */
    if (cartHasExpress && !locked && !isOptionOn(opt)) {
      opt.classList.add('mpv-express-locked');
      var note = document.createElement('div');
      note.className = 'mpv-express-note';
      note.innerHTML = '<span>Вече избра веднъж бърза гравюра! Тя се прилага на цялата кошница! ❤️</span>';
      opt.appendChild(note);
    } else if (!cartHasExpress && locked) {
      opt.classList.remove('mpv-express-locked');
      var n = opt.querySelector('.mpv-express-note');
      if (n) n.remove();
    }
  }

  function poll() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartHasExpress = (cart.items || []).some(function (it) {
          var props = it.properties || {};
          for (var k in props) {
            if (EXPRESS_RX.test(k) || EXPRESS_RX.test(String(props[k] || ''))) return true;
          }
          return false;
        });
        apply();
      })
      .catch(function () { /* мрежова грешка - не пипай нищо */ });
  }

  poll();
  setInterval(poll, 4000);
})();


/* ── BIS таймер: тиктакащ брояч до custom.bis_timer_until ──
   Скрит [hidden] по подразбиране: CDN кешira Liquid рендера, затова
   валидността се преценява и клиентски - минал срок = не се показва. */
(function () {
  if (window.__mpvBisTimer) return;
  window.__mpvBisTimer = true;
  var box = document.querySelector('[data-mpv2-bis-timer]');
  if (!box) return;
  var clock = box.querySelector('[data-mpv2-bis-clock]');
  var until = parseInt(box.getAttribute('data-until') || '0', 10) * 1000;
  if (!clock || !until) return;
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function tick() {
    var left = until - Date.now();
    if (left <= 0) { box.hidden = true; clearInterval(iv); return; }
    var s = Math.floor(left / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    var sec = s % 60;
    var word = d === 1 ? 'ден' : 'дни';
    clock.textContent = d > 0
      ? d + ' ' + word + ' ' + pad(h) + ':' + pad(m) + ':' + pad(sec)
      : pad(h) + ':' + pad(m) + ':' + pad(sec);
    box.hidden = false;
  }
  var iv = setInterval(tick, 1000);
  tick();
})();


/* ── Верижките (колие/гривна с цветя): червения Easify текст → бранд spec-карта ──
   Easify рендира в option описанието червен <strong> от типа:
   "Верижка от Сребро 925 - Дължина: 40+8см (Регулеруема) / Дебелина: 1мм / Тежка: 1гр."
   Текстът е конфигуриран в Easify (не в темата), затова го прехващаме
   клиентски и го представяме като кремава карта с чипове в бранд стила.
   Идемпотентно (data-wy-chainspec) + поллинг, защото Easify рендира късно. */
(function () {
  if (window.__wyChainSpec) return;
  window.__wyChainSpec = true;

  // Не изброявай имена - Easify ги мени (членуваното Верижка_та_ от Сребро 925
  // чупеше стария списък и червеният суров текст оставаше на страницата).
  // Заглавието е каквото стои ПРЕДИ Дължина; трите мерки са котвата.
  var RX = /^\s*(.{0,80}?)\s*[-–—:]*\s*Дължина:?\s*([^/]+?)\s*\/\s*Дебелина:?\s*([^/]+?)\s*\/\s*Теж(?:ка|ко|ло):?\s*([\d.,]+\s*гр)/;

  function chip(label, value) {
    var el = document.createElement('span');
    el.setAttribute('style',
      'display:inline-flex;align-items:center;gap:4px;background:#fff;' +
      'border:1px solid rgba(114,47,55,.22);border-radius:999px;' +
      'padding:3px 10px;font-size:12px;line-height:1.3;color:#722F37;white-space:nowrap;');
    el.innerHTML = '<span style="opacity:.65;font-weight:400;">' + label + '</span>' +
      '<span style="font-weight:700;">' + value + '</span>';
    return el;
  }

  function transform(p) {
    var m = (p.textContent || '').match(RX);
    if (!m) return;
    // Махаме определителния член, за да не пише Верижката на картата,
    // и подрязваме разделителите, ако Easify е сложил тире накрая.
    // ⚠ БЕЗ \b - в JS границата на думата е само ASCII и след кирилица
    // не се задейства. Затова изрично: следва не-кирилица или край.
    var title = (m[1] || 'Верижка')
      .replace(/^Верижката(?=[^а-яА-Я]|$)/, 'Верижка')
      .replace(/^Посребрената(?=[^а-яА-Я]|$)/, 'Посребрена')
      .replace(/[-–—:\s]+$/, '')
      .trim() || 'Верижка';
    var len = m[2].replace('Регулеруема', 'Регулируема').replace(/см/, ' см').replace(/\s+/g, ' ').trim();
    var thick = m[3].replace(/мм/, ' мм').replace(/\s+/g, ' ').trim();
    var weight = m[4].replace(/гр/, ' гр').replace(/\s+/g, ' ').trim();

    var card = document.createElement('div');
    card.setAttribute('data-wy-chainspec', '1');
    card.setAttribute('style',
      'background:#FAF3E7;border:1px solid rgba(114,47,55,.16);border-radius:10px;' +
      'padding:10px 12px;margin:6px 0 2px;');
    var titleEl = document.createElement('div');
    titleEl.setAttribute('style',
      'font-weight:700;color:#722F37;font-size:13px;margin-bottom:7px;letter-spacing:.01em;');
    titleEl.textContent = title;
    var row = document.createElement('div');
    row.setAttribute('style', 'display:flex;flex-wrap:wrap;gap:6px;');
    row.appendChild(chip('Дължина', len));
    row.appendChild(chip('Дебелина', thick));
    row.appendChild(chip('Тегло', weight));
    card.appendChild(titleEl);
    card.appendChild(row);
    p.replaceWith(card);
  }

  function scan() {
    var ps = document.querySelectorAll('.po-paragraph-content p:not([data-wy-chainspec])');
    for (var i = 0; i < ps.length; i++) {
      if (ps[i].closest('[data-wy-chainspec]')) continue;
      transform(ps[i]);
    }
  }

  scan();
  var tries = 0;
  var iv = setInterval(function () {
    scan();
    if (++tries > 45) clearInterval(iv); // Easify рендира до ~10-15s след load;
    // 45 x 800ms = 36s - на бавен мобилен нет 16s не стигаха и картата не се сглобяваше
  }, 800);
})();
